import { useState, useMemo, useEffect } from "react";
import { Star, ThumbsUp, ThumbsDown, MoreHorizontal, ExternalLink, Trash2, ArrowUp, ArrowDown, Minus, Clock, CircleSlash, ChevronDown, ChevronRight } from "lucide-react";
import { MonthPicker } from "@/components/ui/month-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Review, BusinessOpeningHour } from "@/types";
import {
  isToday,
  isYesterday,
  format,
  startOfDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  isWeekend,
  eachDayOfInterval,
  min,
  max,
} from "date-fns";
import { useLiveShiftValidation } from "@/hooks/scheduling/useShiftValidationStatus";
import { enrichDayValidations } from "@/lib/reviews/day-statistics-enricher";
import { isHoliday } from "@/lib/scheduling/holidays";
import { formatOpeningHours } from "@/lib/business/opening-hours-utils";
import { supabase } from "@/integrations/supabase/client";
import { DayStatsBadges } from "./DayStatsBadges";
import { StaffingSummary } from "./StaffingSummary";
import { fetchAndStoreWeatherForDates, formatWeatherForDisplay, type WeatherDisplayData } from "@/lib/weather/weather-service";
import { cn } from "@/lib/core/utils";
import type { EnrichedDayStats } from "@/lib/reviews/day-statistics-enricher";
import { Skeleton } from "@/components/ui/skeleton";

interface ReviewsListProps {
  reviews: Review[];
  filterDescription: string;
  onViewDetails: (review: Review) => void;
  onDeleteReview: (review: Review) => void;
  onBulkDelete?: (reviewIds: string[]) => void;
  businessId?: string;
  countryCode?: string;
  businessCreatedAt?: string; // For validation
  filterControls?: React.ReactNode; // Add optional filter controls slot
}

const ReviewsList = ({ reviews, filterDescription, onViewDetails, onDeleteReview, onBulkDelete, businessId, countryCode, businessCreatedAt, filterControls }: ReviewsListProps) => {
  // Bulk selection state
  const [selectedReviews, setSelectedReviews] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Month selector state
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

  // Empty days state - always show empty days
  const showEmptyDays = true; // Always enabled
  const [expandedEmptyDays, setExpandedEmptyDays] = useState<Set<string>>(new Set());

  // Extract unique dates from reviews for shift validation
  const reviewDates = useMemo(() => {
    if (reviews.length === 0) return [];
    // Get unique dates from all reviews
    const uniqueDates = Array.from(new Set(
      reviews.map(review => format(startOfDay(parseISO(review.date)), 'yyyy-MM-dd'))
    )).map(dateStr => parseISO(dateStr));
    return uniqueDates;
  }, [reviews]);

  // Fetch shift validation data for all review dates with employee data
  const { validationResults, isLoading: shiftsLoading } = useLiveShiftValidation({
    businessId: businessId || '',
    dates: reviewDates,
    includeOpeningHours: true,
    includeEmployeeData: true, // Fetch employee names for staffing info
    enabled: Boolean(businessId) && reviewDates.length > 0,
    refreshInterval: 0 // Disable auto-refresh - reviews are historical data
  });

  // Debug logging for shift validation results
  useEffect(() => {
    console.log('[ReviewsList] Shift validation results:', {
      validationResultsSize: validationResults.size,
      reviewDatesCount: reviewDates.length,
      businessId,
      shiftsLoading,
      validationKeys: Array.from(validationResults.keys())
    });
  }, [validationResults, reviewDates.length, businessId, shiftsLoading]);

  // Weather data state (must be declared before enrichedStats useMemo)
  const [weatherData, setWeatherData] = useState<Map<string, WeatherDisplayData>>(new Map());
  const [weatherLoading, setWeatherLoading] = useState(false);

  // Enrich validation results with holiday, weekend, and weather information
  const enrichedStats = useMemo(() => {
    if (validationResults.size === 0) {
      return new Map();
    }
    // Only enrich with holidays if country code is set
    // If no country code, return raw validation results without holiday/weekend enrichment
    if (!countryCode) {
      console.log('[ReviewsList] No country code provided, showing shift data without holiday context');
      return enrichDayValidations(validationResults, countryCode, weatherData);
    }
    console.log('[ReviewsList] Enriching validation results with country code:', countryCode);
    const enriched = enrichDayValidations(validationResults, countryCode, weatherData);
    return enriched;
  }, [validationResults, countryCode, weatherData]);

  // Fetch opening hours for the business
  const [openingHours, setOpeningHours] = useState<BusinessOpeningHour[] | null>(null);
  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (!businessId) return;

      const { data, error } = await supabase
        .from('business_opening_hours')
        .select('*')
        .eq('business_id', businessId);

      if (error) {
        console.error('Error fetching opening hours:', error);
        return;
      }

      setOpeningHours(data || []);
    };

    fetchOpeningHours();
  }, [businessId]);

  // Fetch weather data for review dates
  useEffect(() => {
    const fetchWeatherData = async () => {
      if (!businessId || reviews.length === 0) return;

      // Get business location from first review's business data
      const { data: businessData, error: businessError } = await supabase
        .from('businesses')
        .select('address, latitude, longitude')
        .eq('id', businessId)
        .single();

      if (businessError || !businessData) {
        console.warn('[ReviewsList] Could not fetch business location for weather data');
        return;
      }

      // Check if we have coordinates, if not try to geocode from address
      const lat = businessData.latitude;
      const lon = businessData.longitude;

      if (!lat || !lon) {
        console.warn('[ReviewsList] Business has no coordinates, cannot fetch weather');
        return;
      }

      setWeatherLoading(true);

      try {
        // Extract unique dates from reviews
        const uniqueDates = Array.from(new Set(
          reviews.map(r => format(startOfDay(parseISO(r.date)), 'yyyy-MM-dd'))
        )).map(dateStr => parseISO(dateStr));

        console.log(`[ReviewsList] Fetching weather for ${uniqueDates.length} unique dates`);

        // Fetch weather data (will check database first, then API if needed)
        const weatherMap = await fetchAndStoreWeatherForDates(
          uniqueDates,
          lat,
          lon,
          businessId
        );

        // Format weather data for display
        const formattedWeatherMap = new Map<string, WeatherDisplayData>();
        weatherMap.forEach((data, dateKey) => {
          const formatted = formatWeatherForDisplay(data);
          if (formatted) {
            formattedWeatherMap.set(dateKey, formatted);
          }
        });

        setWeatherData(formattedWeatherMap);
        console.log(`[ReviewsList] Weather data loaded for ${formattedWeatherMap.size} dates`);
      } catch (error) {
        console.error('[ReviewsList] Error fetching weather data:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeatherData();
  }, [businessId, reviews]);

  // Helper function to get formatted opening hours for a specific date
  const getOpeningHoursForDate = useMemo(() => {
    return (date: Date): string => {
      if (!openingHours || openingHours.length === 0) {
        return "Hours not available";
      }

      // Get day name from date (e.g., "Monday", "Tuesday", etc.)
      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      const dayName = dayNames[date.getDay()];

      // Use the utility function to format the hours
      return formatOpeningHours(openingHours, dayName);
    };
  }, [openingHours]);

  // Group reviews by day
  const groupedReviews = useMemo(() => {
    console.log('[ReviewsList] groupedReviews recalculating, showEmptyDays:', showEmptyDays, 'reviews.length:', reviews.length, 'selectedMonth:', selectedMonth);
    const groups: { [key: string]: { label: string; reviews: Review[]; sortDate: Date; isEmpty: boolean } } = {};

    // First, add all reviews from the selected month to their respective days
    reviews.forEach(review => {
      const reviewDate = parseISO(review.date);
      const reviewMonthStart = startOfMonth(reviewDate);
      const selectedMonthStart = startOfMonth(selectedMonth);

      // Only include reviews from the selected month
      if (reviewMonthStart.getTime() !== selectedMonthStart.getTime()) {
        return;
      }

      const dayKey = format(startOfDay(reviewDate), 'yyyy-MM-dd');

      if (!groups[dayKey]) {
        let label: string;
        if (isToday(reviewDate)) {
          label = 'Today';
        } else if (isYesterday(reviewDate)) {
          label = 'Yesterday';
        } else {
          label = format(reviewDate, 'EEEE, MMMM d');
        }

        groups[dayKey] = {
          label,
          reviews: [],
          sortDate: startOfDay(reviewDate),
          isEmpty: false
        };
      }

      groups[dayKey].reviews.push(review);
    });

    // Generate all days in the selected month
    if (showEmptyDays) {
      console.log('[ReviewsList] Generating all days for selected month...');
      const monthStart = startOfMonth(selectedMonth);
      const monthEnd = endOfMonth(selectedMonth);
      console.log('[ReviewsList] Month range:', monthStart, 'to', monthEnd);

      // Generate all dates in the selected month
      const allDates = eachDayOfInterval({ start: monthStart, end: monthEnd });
      console.log('[ReviewsList] Generated', allDates.length, 'dates in month');

      allDates.forEach(date => {
        const dayKey = format(startOfDay(date), 'yyyy-MM-dd');

        // Only add if this date doesn't already have reviews
        if (!groups[dayKey]) {
          let label: string;
          if (isToday(date)) {
            label = 'Today';
          } else if (isYesterday(date)) {
            label = 'Yesterday';
          } else {
            label = format(date, 'EEEE, MMMM d');
          }

          groups[dayKey] = {
            label,
            reviews: [],
            sortDate: startOfDay(date),
            isEmpty: true
          };
        }
      });
    }

    // Sort groups by date (most recent first) and convert to array
    const result = Object.values(groups).sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime());
    const emptyCount = result.filter(g => g.isEmpty).length;
    const withReviewsCount = result.filter(g => !g.isEmpty).length;
    console.log('[ReviewsList] Final groups:', result.length, 'total (', emptyCount, 'empty,', withReviewsCount, 'with reviews)');
    console.log('[ReviewsList] Sample groups:', result.slice(0, 3).map(g => ({ label: g.label, isEmpty: g.isEmpty, reviewCount: g.reviews.length })));
    return result;
  }, [reviews, selectedMonth]);

  // Calculate cumulative rating changes for each day
  const dayRatingChanges = useMemo(() => {
    const changes: { [key: string]: { beforeAvg: number; afterAvg: number; beforeCount: number; afterCount: number } } = {};

    // Sort all reviews by date (oldest first) to calculate running averages
    const sortedReviews = [...reviews].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    sortedReviews.forEach((review, index) => {
      const reviewDate = parseISO(review.date);
      const dayKey = format(startOfDay(reviewDate), 'yyyy-MM-dd');

      if (!changes[dayKey]) {
        // Calculate average before this day
        const reviewsBefore = sortedReviews.slice(0, index);
        const beforeCount = reviewsBefore.length;
        const beforeAvg = beforeCount > 0
          ? reviewsBefore.reduce((sum, r) => sum + r.rating, 0) / beforeCount
          : 0;

        // Calculate average including this day
        const reviewsUpToEndOfDay = sortedReviews.slice(0, index + 1);
        let lastIndexOfDay = index;

        // Find all reviews on this day
        for (let i = index + 1; i < sortedReviews.length; i++) {
          const nextReviewDate = parseISO(sortedReviews[i].date);
          const nextDayKey = format(startOfDay(nextReviewDate), 'yyyy-MM-dd');
          if (nextDayKey === dayKey) {
            lastIndexOfDay = i;
          } else {
            break;
          }
        }

        const reviewsIncludingDay = sortedReviews.slice(0, lastIndexOfDay + 1);
        const afterCount = reviewsIncludingDay.length;
        const afterAvg = reviewsIncludingDay.reduce((sum, r) => sum + r.rating, 0) / afterCount;

        changes[dayKey] = {
          beforeAvg: beforeCount > 0 ? beforeAvg : afterAvg, // If first day, show same as after
          afterAvg,
          beforeCount,
          afterCount
        };
      }
    });

    return changes;
  }, [reviews]);

  // Calculate submission count (1st, 2nd, 3rd, etc.) based on customer email
  const submissionCounts = useMemo(() => {
    const emailCountMap = new Map<string, number>();
    const reviewCounts = new Map<string, number>();

    // Sort reviews by date (oldest first)
    const sortedReviews = [...reviews].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    sortedReviews.forEach(review => {
      if (!review.customerEmail) {
        reviewCounts.set(review.id, 0); // 0 means no email (unknown)
        return;
      }

      const currentCount = emailCountMap.get(review.customerEmail) || 0;
      const thisReviewNumber = currentCount + 1;

      emailCountMap.set(review.customerEmail, thisReviewNumber);
      reviewCounts.set(review.id, thisReviewNumber);
    });

    return reviewCounts;
  }, [reviews]);

  // Helper function to convert number to ordinal (1st, 2nd, 3rd, etc.)
  const getOrdinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Helper function to get border color class based on day status
  const getDayBorderColor = (date: Date, dayStats: EnrichedDayStats | undefined): string => {
    // Priority order matches BaseShiftCalendar logic
    if (isToday(date)) {
      return 'border-2 border-orange-500';
    }

    // Check if closed (no opening hours or status is 'closed')
    if (dayStats?.status === 'closed') {
      return 'border-2 border-gray-400 dark:border-gray-500';
    }

    // Check for holiday (non-working day)
    if (dayStats?.isHoliday && dayStats?.isNonWorkingDay) {
      return 'border-2 border-destructive';
    }

    // Check for weekend
    if (dayStats?.isWeekend) {
      return 'border-2 border-primary'; // Purple (#5c3580)
    }

    // Regular day - default border
    return 'border';
  };

  // Bulk selection utility functions
  const toggleSelectAll = () => {
    if (selectedReviews.length === reviews.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(reviews.map(r => r.id));
    }
  };

  const toggleSelectReview = (reviewId: string) => {
    setSelectedReviews(prev => 
      prev.includes(reviewId) 
        ? prev.filter(id => id !== reviewId)
        : [...prev, reviewId]
    );
  };

  const handleBulkDelete = () => {
    if (onBulkDelete && selectedReviews.length > 0) {
      onBulkDelete(selectedReviews);
      setSelectedReviews([]);
      setShowBulkActions(false);
    }
  };

  const isAllSelected = selectedReviews.length === reviews.length && reviews.length > 0;
  const isIndeterminate = selectedReviews.length > 0 && selectedReviews.length < reviews.length;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
    const timePart = date.toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit'
    });
    return `${datePart}, ${timePart}`;
  };

  // Removed handlePublish function

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "negative":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={16}
            className={i < rating ? "text-gold-500 fill-gold-500 stroke-yellow-700" : "text-gray-300 stroke-yellow-700"}
          />
        ))}
      </div>
    );
  };

  const renderSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <ThumbsUp size={14} className="mr-1" />;
      case "negative":
        return <ThumbsDown size={14} className="mr-1" />;
      default:
        return null;
    }
  };

  // Toggle empty day expansion
  const toggleEmptyDayExpansion = (dateKey: string) => {
    setExpandedEmptyDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  // State for empty day enriched data
  const [emptyDayEnrichedData, setEmptyDayEnrichedData] = useState<Map<string, EnrichedDayStats>>(new Map());
  const [loadingEmptyDays, setLoadingEmptyDays] = useState<Set<string>>(new Set());

  // Fetch enriched data for an empty day when expanded
  useEffect(() => {
    const fetchEmptyDayData = async (dateKey: string, date: Date) => {
      // Skip if already loaded or currently loading
      if (emptyDayEnrichedData.has(dateKey) || loadingEmptyDays.has(dateKey)) {
        return;
      }

      setLoadingEmptyDays(prev => new Set(prev).add(dateKey));

      try {
        // Fetch shift validation data for this single date
        const { data: shiftData, error: shiftError } = await supabase
          .from('shifts')
          .select('*, employees(id, name)')
          .eq('business_id', businessId)
          .gte('start_time', format(date, 'yyyy-MM-dd') + 'T00:00:00')
          .lte('start_time', format(date, 'yyyy-MM-dd') + 'T23:59:59');

        const isWeekendDay = isWeekend(date);
        const holidayInfo = countryCode ? isHoliday(date, countryCode) : null;

        // Get weather data if available
        const weather = weatherData.get(dateKey);

        // Create enriched stats for this day
        const dayStats: EnrichedDayStats = {
          date,
          dateKey,
          status: shiftData && shiftData.length > 0 ? 'green' : 'red',
          hasShifts: Boolean(shiftData && shiftData.length > 0),
          allShiftsAssigned: Boolean(shiftData && shiftData.length > 0),
          shiftsCount: shiftData?.length || 0,
          openingHoursCovered: false,
          coverageGaps: [],
          validationMessage: shiftData && shiftData.length > 0 ? 'Shifts scheduled' : 'No shifts scheduled',
          isWeekend: isWeekendDay,
          isHoliday: Boolean(holidayInfo),
          holidayName: holidayInfo?.name,
          isNonWorkingDay: holidayInfo?.isNonWorkingDay,
          weather,
          hasIssues: false,
          allStaffOnTime: false
        };

        setEmptyDayEnrichedData(prev => new Map(prev).set(dateKey, dayStats));
      } catch (error) {
        console.error('Error fetching empty day data:', error);
      } finally {
        setLoadingEmptyDays(prev => {
          const newSet = new Set(prev);
          newSet.delete(dateKey);
          return newSet;
        });
      }
    };

    // Fetch data for all currently expanded empty days
    expandedEmptyDays.forEach(dateKey => {
      const group = groupedReviews.find(g => format(g.sortDate, 'yyyy-MM-dd') === dateKey);
      if (group && group.isEmpty) {
        fetchEmptyDayData(dateKey, group.sortDate);
      }
    });
  }, [expandedEmptyDays, businessId, countryCode, groupedReviews, emptyDayEnrichedData, loadingEmptyDays, weatherData]);

  // EmptyDayRow Component
  const EmptyDayRow = ({ group }: { group: { label: string; sortDate: Date; isEmpty: boolean } }) => {
    const dateKey = format(group.sortDate, 'yyyy-MM-dd');
    const isExpanded = expandedEmptyDays.has(dateKey);
    const isLoading = loadingEmptyDays.has(dateKey);
    const dayStats = emptyDayEnrichedData.get(dateKey);

    if (isExpanded) {
      // Show full day card when expanded
      return (
        <div className="flex gap-4 mb-6 min-h-0">
          {/* Date Header - Left Side */}
          <div className="w-64 flex-shrink-0">
            <div className={cn(
              "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-3 rounded-lg h-full",
              "border border-dashed border-muted-foreground/30"
            )}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-muted-foreground">{group.label}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleEmptyDayExpansion(dateKey)}
                  className="h-6 w-6 p-0"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <CircleSlash className="h-3 w-3" />
                0 reviews
              </p>

              {/* Opening Hours */}
              {businessId && (
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground font-medium">
                    {getOpeningHoursForDate(group.sortDate)}
                  </span>
                </div>
              )}

              {/* Day Statistics */}
              {businessId && (
                <div className="mt-3 space-y-2">
                  {isLoading ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="h-5 w-20" />
                      </div>
                      <div className="flex items-start gap-2">
                        <Skeleton className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 space-y-1">
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </div>
                    </div>
                  ) : dayStats ? (
                    <>
                      <DayStatsBadges stats={dayStats} />
                      <StaffingSummary stats={dayStats} />
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      Loading day information...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Empty message */}
          <div className="flex-1 flex items-center justify-center min-h-[120px] border border-dashed border-muted-foreground/20 rounded-lg">
            <div className="text-center text-muted-foreground">
              <CircleSlash className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No reviews received this day</p>
            </div>
          </div>
        </div>
      );
    }

    // Compact collapsed state
    return (
      <div
        className="flex items-center gap-3 px-4 py-2 mb-2 border border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => toggleEmptyDayExpansion(dateKey)}
      >
        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <CircleSlash className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1">
          <span className="text-sm font-medium text-muted-foreground">{group.label}</span>
          <span className="text-xs text-muted-foreground/70 ml-2">• 0 reviews</span>
        </div>
        <span className="text-xs text-muted-foreground/50">Click to expand</span>
      </div>
    );
  };

  return (
    <Card className="w-full max-w-full">
      <CardHeader className="space-y-4 flex-shrink-0">
        <div className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-medium tracking-tight">{filterDescription}</CardTitle>
            <CardDescription>Manage your customer reviews</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {onBulkDelete && reviews.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                >
                  {showBulkActions ? 'Cancel Selection' : 'Select Multiple'}
                </Button>
                {showBulkActions && selectedReviews.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="flex items-center gap-1"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Selected ({selectedReviews.length})
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Filter Controls Section */}
        <div className="pt-2 border-t">
          <div className="flex items-center gap-4">
            {/* Month Selector */}
            <MonthPicker
              value={selectedMonth}
              onChange={setSelectedMonth}
              businessCreationDate={businessCreatedAt}
              size="sm"
            />

            {/* Custom Filter Controls */}
            {filterControls}
          </div>
        </div>

        {/* Bulk Selection Checkbox */}
        {showBulkActions && reviews.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-gray-600">
              {selectedReviews.length === 0
                ? 'Select all reviews'
                : `${selectedReviews.length} of ${reviews.length} selected`}
            </span>
          </div>
        )}
      </CardHeader>
      <CardContent className="w-full max-w-full p-2">
        {reviews.length === 0 ? (
          <div className="text-center text-xl text-white-600 py-8">
            No reviews match the selected filters.
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-265px)] w-full">
            <div className="space-y-6 w-full pr-4">
              {groupedReviews.map((group) => {
              // If this is an empty day, render the compact EmptyDayRow component
              if (group.isEmpty) {
                return <EmptyDayRow key={format(group.sortDate, 'yyyy-MM-dd')} group={group} />;
              }

              // Get enriched stats for this day OR create minimal stats
              const dateKey = format(group.sortDate, 'yyyy-MM-dd');
              let dayStats = enrichedStats.get(dateKey);

              // Debug logging for each day
              console.log(`[ReviewsList] Day ${dateKey}:`, {
                hasDayStats: !!dayStats,
                enrichedStatsSize: enrichedStats.size,
                shiftsCount: dayStats?.shiftsCount,
                hasShifts: dayStats?.hasShifts,
                status: dayStats?.status
              });

              // If no shift data exists, create minimal stats with just weekend/holiday/weather info
              // This should be rare since we now load data for all review dates
              if (!dayStats && businessId) {
                const isWeekendDay = isWeekend(group.sortDate);
                const holidayInfo = countryCode ? isHoliday(group.sortDate, countryCode) : null;

                // Get real weather data from fetched weather map
                const weather = weatherData.get(dateKey);

                // Use 'red' status (no shifts) instead of 'closed' since we don't have opening hours data
                // to determine if the business is actually closed
                dayStats = {
                  date: group.sortDate,
                  dateKey,
                  status: 'red' as const,
                  hasShifts: false,
                  allShiftsAssigned: false,
                  shiftsCount: 0,
                  openingHoursCovered: false,
                  coverageGaps: [],
                  validationMessage: 'No shift data available',
                  isWeekend: isWeekendDay,
                  isHoliday: Boolean(holidayInfo),
                  holidayName: holidayInfo?.name,
                  isNonWorkingDay: holidayInfo?.isNonWorkingDay,
                  weather,
                  hasIssues: false,
                  allStaffOnTime: false
                };
              }

              return (
                <div key={group.label} className="flex gap-4 mb-6 min-h-0">
                  {/* Date Header - Left Side */}
                  <div className="w-64 flex-shrink-0">
                    <div className={cn(
                      "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 p-3 rounded-lg h-full sticky top-0",
                      getDayBorderColor(group.sortDate, dayStats)
                    )}>
                      <h3 className="text-lg font-semibold text-foreground">{group.label}</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        {group.reviews.length} {group.reviews.length === 1 ? 'review' : 'reviews'}
                      </p>

                      {/* Opening Hours */}
                      {businessId && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs">
                          <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground font-medium">
                            {getOpeningHoursForDate(group.sortDate)}
                          </span>
                        </div>
                      )}

                      {/* Rating Change */}
                      {(() => {
                        const change = dayRatingChanges[dateKey];
                        if (!change || change.beforeCount === 0) return null;

                        const ratingDiff = change.afterAvg - change.beforeAvg;
                        const countDiff = change.afterCount - change.beforeCount;
                        const isPositive = ratingDiff > 0;
                        const isNegative = ratingDiff < 0;
                        const isNeutral = ratingDiff === 0;

                        return (
                          <div className="mt-2 space-y-1">
                            {/* Rating Average Change */}
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="text-muted-foreground">Rating:</span>
                              <span className="font-medium">{change.beforeAvg.toFixed(1)}</span>
                              {isPositive && <ArrowUp className="h-3 w-3 text-green-600" />}
                              {isNegative && <ArrowDown className="h-3 w-3 text-red-600" />}
                              {isNeutral && <Minus className="h-3 w-3 text-gray-400" />}
                              <span className={`font-medium ${isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'}`}>
                                {change.afterAvg.toFixed(1)}
                              </span>
                            </div>
                            {/* Count Change */}
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="text-muted-foreground">Count:</span>
                              <span className="font-medium">{change.beforeCount}</span>
                              <ArrowUp className="h-3 w-3 text-blue-600" />
                              <span className="font-medium text-blue-600">{change.afterCount}</span>
                              <span className="text-muted-foreground">(+{countDiff})</span>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Day Statistics */}
                      {businessId && (
                        <div className="mt-3 space-y-2">
                          {(shiftsLoading || weatherLoading) ? (
                            <div className="space-y-2">
                              {/* Badges skeleton */}
                              <div className="flex flex-wrap items-center gap-2">
                                <Skeleton className="h-5 w-24" />
                                <Skeleton className="h-5 w-20" />
                                <Skeleton className="h-5 w-28" />
                              </div>
                              {/* Staffing info skeleton */}
                              <div className="flex items-start gap-2">
                                <Skeleton className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 space-y-1">
                                  <Skeleton className="h-4 w-32" />
                                  <Skeleton className="h-3 w-40" />
                                </div>
                              </div>
                            </div>
                          ) : dayStats ? (
                            <>
                              <DayStatsBadges stats={dayStats} />
                              <StaffingSummary stats={dayStats} />
                            </>
                          ) : (
                            <div className="text-xs text-muted-foreground">
                              No data available for this day
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reviews in this group - Right Side */}
                  <div className="flex-1 space-y-4 min-h-0">
                    {group.reviews.map((review) => {
                    return (
                      <div
                        key={review.id}
                        className="border rounded-md py-2 px-3 flex flex-col sm:flex-row justify-between w-full gap-4"
                      >
                        {showBulkActions && (
                          <div className="flex items-start pt-1">
                            <Checkbox
                              checked={selectedReviews.includes(review.id)}
                              onCheckedChange={() => toggleSelectReview(review.id)}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between">
                            <div className="min-w-0">
                              <p className="font-semibold break-words">
                                {review.customerName
                                  ? review.customerEmail
                                    ? `${review.customerName} (${review.customerEmail})`
                                    : review.customerName
                                  : review.customerEmail
                                  ? review.customerEmail
                                  : "Anonymous"}
                              </p>
                              <div className="flex items-center space-x-2 mt-1">
                                {renderStars(review.rating)}
                                <span className="text-xs text-gray-500">{formatDate(review.date)}</span>
                              </div>
                            </div>
                          </div>
                          <p className="mt-2 text-white-700 break-words">{review.text}</p>
                          <div className="mt-1 text-xs text-gray-500 flex items-center gap-2">
                            <span>{review.formTitle ? `Form: ${review.formTitle}` : review.tagName ? `Tag: ${review.tagName}` : 'Direct submission'}</span>
                            {(() => {
                              const count = submissionCounts.get(review.id);
                              if (!count || count === 0) return null; // No email, don't show badge

                              // Color coding based on review count
                              let badgeColor = "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
                              if (count === 1) {
                                badgeColor = "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
                              } else if (count === 2) {
                                badgeColor = "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
                              } else if (count === 3) {
                                badgeColor = "bg-primary/10 text-primary";
                              } else if (count >= 10) {
                                badgeColor = "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300";
                              } else {
                                badgeColor = "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300";
                              }

                              return (
                                <Badge variant="outline" className={`${badgeColor} text-xs`}>
                                  {getOrdinal(count)} review
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0 sm:ml-4">
                           {/* Enable Reply Button and link to onViewDetails */}
                           <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => onViewDetails(review)}>
                             {review.reply ? 'Edit Reply' : 'Reply'}
                           </Button>
                           <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 ml-2">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => onViewDetails(review)}>View Details</DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600" onSelect={() => onDeleteReview(review)}>Delete</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewsList;
